import { availableCards } from 'themes'

const generateXCurrency = (id: string, currency: IAvailableCurrencies): IAsset => ({
    balance: { currencyCode: currency, value: 0.0, valueString: '0.0' },
    cardInfo: null,
    cryptoInfo: null,
    exchangedValue: { rate: 1.0, currencyCode: currency, amount: 0.0 },
    friendlyName: `AAAAAA ${currency}`,
    ibanInfo: null,
    identifier: id,
    label: null,
    name: 'EUR',
    status: 'Active',
    type: 'XCurrency',
    isDebitLocked: false,
    debitUnlockThresholdAmount: null,
})

const generateIBAN = (id: string, types: ('Internal' | 'Sepa' | 'International')[]): IAsset => ({
    balance: { currencyCode: 'EUR', value: 0.0, valueString: '0' },
    cardInfo: null,
    cryptoInfo: null,
    exchangedValue: { rate: 1.0, currencyCode: 'EUR', amount: 0.0 },
    friendlyName: 'LT000000000000000000',
    ibanInfo: { iban: 'LT000000000000000000', type: 'Foxpay', supportedTransferTypes: types, bankName: 'Foxpay, UAB' },
    identifier: id,
    label: null,
    name: 'LT000000000000000000',
    status: 'Active',
    type: 'Iban',
    isDebitLocked: false,
    debitUnlockThresholdAmount: null,
})

const generatePaymentCard = (id: string, type: 'Physical' | 'Virtual', cardImg: keyof typeof availableCards): IAsset => ({
    balance: { currencyCode: 'EUR', value: 0.0, valueString: '0.0' },
    cardInfo: { status: 'Active', expiryDate: '2035-01-01T00:00:00Z', type: type, number: '123456******0000', code: cardImg, iban: null },
    cryptoInfo: null,
    exchangedValue: { rate: 1.0, currencyCode: 'EUR', amount: 0.0 },
    friendlyName: '123456******0000',
    ibanInfo: null,
    identifier: id,
    label: null,
    name: '123456******0000',
    status: 'Active',
    type: 'CreditCard',
    isDebitLocked: false,
    debitUnlockThresholdAmount: null,
})

const generateDigitalAsset = (id: string, currency: IAvailableCryptocurrencies): IAsset => ({
    balance: { currencyCode: currency, value: 0.0, valueString: '0.0' },
    cardInfo: null,
    cryptoInfo: { address: '0x0000000000000000000000000000000000000000' },
    exchangedValue: { rate: 1.0, currencyCode: 'EUR', amount: 0.0 },
    friendlyName: '0x0000000000000000000000000000000000000000',
    ibanInfo: null,
    identifier: id,
    label: null,
    name: currency,
    status: 'Active',
    type: 'DigitalAsset',
    isDebitLocked: false,
    debitUnlockThresholdAmount: null,
})

const generateLimitedToken = (id: string, currency: IAvailableTokenWalletCurrencies): IAsset => ({
    balance: { currencyCode: currency, value: 0.0, valueString: '0' },
    cardInfo: null,
    cryptoInfo: null,
    exchangedValue: { rate: 0.0, currencyCode: 'EUR', amount: 0.0 },
    friendlyName: `AAAAAA ${currency}`,
    ibanInfo: null,
    identifier: id,
    label: null,
    name: currency,
    status: 'Active',
    type: 'LimitedToken',
    isDebitLocked: false,
    debitUnlockThresholdAmount: null,
})

const ALL_X_CURRENCIES = ['EUR', 'USD', 'CHF', 'GBP'] as const
const ALL_DIGITAL_CURRENCIES = ['USDC', 'BTC', 'USDT', 'ETH', 'TRX', 'SOL'] as const
const ALL_LIMITED_TOKENS = ['AIR777', 'MT777', 'DWRLD', 'XRP', 'ADA'] as const

let GENERATED_ASSETS: IAssets
let GENERATED_DASHBOARD: IMockedDashboard

export const generateMockAssets = (): [IAssets, IMockedDashboard] => {
    if (!!GENERATED_ASSETS && !!GENERATED_DASHBOARD) {
        return [GENERATED_ASSETS, GENERATED_DASHBOARD]
    }

    const assets: IAssets = []
    const dashboard: IMockedDashboard = {
        id: '1',
        name: 'Main',
        type: 'Main',
        sections: [],
    }

    // Can turn off some assets for specific WL
    // if (themeConfig.MOCK_X_CURRENCIES_ENABLED) {
    let ids = []
    ALL_X_CURRENCIES.forEach((currency, i) => {
        assets.push(generateXCurrency(i.toString(), currency))
        ids.push({ identifier: i.toString() })
    })

    dashboard.sections.push({
        assets: ids,
        id: '1',
        name: 'Currencies',
        type: 'Money',
        defaultNameKey: 'SectionNameChange.Currencies',
    })
    // }

    ids = []
    ALL_DIGITAL_CURRENCIES.forEach((currency, i) => {
        assets.push(generateDigitalAsset((10 + i).toString(), currency))
        ids.push({ identifier: (10 + i).toString() })
    })
    dashboard.sections.push({
        assets: ids,
        id: '2',
        name: 'Digital Assets',
        type: 'Crypto',
        defaultNameKey: 'SectionNameChange.Digital Assets',
    })

    assets.push(generateIBAN('100', ['Internal', 'Sepa']))
    assets.push(generateIBAN('101', ['Internal', 'Sepa']))
    dashboard.sections.push({
        assets: [{ identifier: '100' }, { identifier: '101' }],
        id: '3',
        name: 'Bank Accounts',
        type: 'BankAccounts',
        defaultNameKey: 'SectionNameChange.Bank Accounts',
    })

    assets.push(generatePaymentCard('1000', 'Physical', 'physical.black.limited'))
    assets.push(generatePaymentCard('1001', 'Virtual', 'virtual.default'))
    dashboard.sections.push({
        assets: [{ identifier: '1000' }, { identifier: '1001' }],
        id: '4',
        name: 'Payment Cards',
        type: 'Cards',
        defaultNameKey: 'payment cards',
    })

    // ids = []
    // ALL_LIMITED_TOKENS.forEach((currency, i) => {
    //     assets.push(generateLimitedToken((10000 + i).toString(), currency))
    //     ids.push({ identifier: (10000 + i).toString() })
    // })
    // dashboard.sections.push({
    //     assets: ids,
    //     id: '5',
    //     name: 'Private Wallets',
    //     type: 'LimitedTokens',
    //     defaultNameKey: 'SectionNameChange.Private Wallets',
    // })

    // Caching generated results
    GENERATED_ASSETS = assets
    GENERATED_DASHBOARD = dashboard
    return [assets, dashboard]
}
